import api from './api';
import config from '../config';

// Resolve /api/photos/<id> paths into fully-qualified URLs so <img> tags
// hit the API host correctly in both dev (CRA on :3000, API on :5000) and
// prod (single origin via Firebase rewrite).
export const photoUrl = (photoOrPath) => {
  if (!photoOrPath) return '';
  const path = typeof photoOrPath === 'string' ? photoOrPath : photoOrPath.url;
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = (config.API_BASE_URL || '').replace(/\/api\/?$/, '');
  return `${base}${path}`;
};

const HEIC_EXT = /\.(heic|heif)$/i;
const HEIC_MIME = /^image\/(heic|heif)$/i;

const isHeic = (file) => HEIC_MIME.test(file.type || '') || HEIC_EXT.test(file.name || '');

// Convert a HEIC/HEIF file to JPEG using only browser-native decoding.
// Works when the browser can render HEIC in an <img> tag (Safari on iOS
// and macOS). Rejects on browsers that can't (Chrome/Firefox on desktop),
// so the caller can show a clear "please convert" message.
//
// This path was chosen deliberately over JS libraries like heic2any or
// heic-to: those bundle a JS build of libheif, which is LGPL-3.0. Using
// the browser's own decoder keeps every runtime dependency permissive.
const convertHeicToJpeg = async (file) => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) {
              reject(new Error('Could not encode the converted photo. Please try again.'));
              return;
            }
            const outName = (file.name || 'photo').replace(HEIC_EXT, '.jpg');
            resolve(new File([blob], outName, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          0.92,
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(
        "This browser can't display HEIC photos. Please either open Car Wash Copilot in Safari, " +
        "or set your iPhone camera to \"Most Compatible\" (Settings → Camera → Formats) so photos are saved as JPEG."
      ));
    };
    img.src = url;
  });
};

// Client-side resize before upload. Phones produce 4-8MB photos; the
// backend only ever displays them at a few hundred pixels wide. Resizing
// to 2000px longest side keeps them retina-crisp and slashes upload size.
export const resizeImage = async (file, maxSide = 2000, quality = 0.82) => {
  // HEIC gets converted to JPEG first via browser-native decoding.
  let source = file;
  if (isHeic(file)) {
    source = await convertHeicToJpeg(file);
  } else if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
    // Unknown / unsupported type — pass through and let the server reject.
    return file;
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        if (scale === 1) {
          resolve(source); // already small enough
          return;
        }
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(source); // fall back to unresized
              return;
            }
            const outName = (source.name || 'photo').replace(/\.(png|webp)$/i, '.jpg');
            resolve(new File([blob], outName, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          quality,
        );
      };
      img.onerror = () => resolve(source);
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(source);
  });
};

export const uploadPhoto = async (file, { contextType, contextId, caption } = {}) => {
  const resized = await resizeImage(file);
  const form = new FormData();
  form.append('file', resized);
  if (contextType) form.append('context_type', contextType);
  if (contextId != null) form.append('context_id', String(contextId));
  if (caption) form.append('caption', caption);
  const res = await api.post('/photos', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const listPhotos = async ({ contextType, contextId } = {}) => {
  const params = {};
  if (contextType) params.context_type = contextType;
  if (contextId != null) params.context_id = contextId;
  const res = await api.get('/photos', { params });
  return res.data;
};

export const deletePhoto = async (publicId) => {
  const res = await api.delete(`/photos/${publicId}`);
  return res.data;
};
